const ROUND_FACTOR = 1000;

export const roundStockQuantity = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }

  const rounded = Math.round((numeric + Number.EPSILON) * ROUND_FACTOR) / ROUND_FACTOR;
  return Object.is(rounded, -0) ? 0 : rounded;
};

export const roundMoney = (value) => {
  if (value == null || value === '') {
    return null;
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }

  const rounded = Math.round((numeric + Number.EPSILON) * 100) / 100;
  return Object.is(rounded, -0) ? 0 : rounded;
};

export const toMoneyInputValue = (value) => {
  const rounded = roundMoney(value);
  return rounded == null ? '' : String(rounded);
};

export const sanitizeDecimalInput = (value, maxDecimals) => {
  if (value === '' || value == null) {
    return '';
  }

  const text = String(value).trim().replace(',', '.');
  if (maxDecimals == null) {
    return text;
  }

  if (text === '-' || text === '.' || text === '-.') {
    return text;
  }

  const decimals = Math.max(0, Number(maxDecimals) || 0);
  const match = text.match(new RegExp(`^-?\\d*(?:\\.\\d{0,${decimals}})?`));
  return match ? match[0] : '';
};

export const formatQuantityNumber = (value) => String(roundStockQuantity(value));

export const stockUnitSuffix = (stockUnit) => {
  if (stockUnit === 'Milliliter') return 'ml';
  if (stockUnit === 'Gram') return 'g';
  return '';
};

export const stockUnitFieldSuffix = (stockUnit) => (
  stockUnitSuffix(stockUnit) || 'un'
);

export const toBaseQuantity = (packageQuantity, contentQuantity) => (
  roundStockQuantity((Number(packageQuantity) || 0) * (Number(contentQuantity) || 0))
);

export const toPackageUnitCost = (baseUnitCost, contentQuantity) => {
  if (baseUnitCost == null || baseUnitCost === '') {
    return null;
  }

  const base = Number(baseUnitCost);
  if (!Number.isFinite(base)) {
    return null;
  }

  const content = Number(contentQuantity) > 0 ? Number(contentQuantity) : 1;
  return roundMoney(base * content);
};

export const toBaseUnitCost = (packageCost, contentQuantity) => {
  if (packageCost == null || packageCost === '') {
    return null;
  }

  const cost = Number(packageCost);
  if (!Number.isFinite(cost)) {
    return null;
  }

  const content = Number(contentQuantity) > 0 ? Number(contentQuantity) : 1;
  return cost / content;
};

export const resolvedContentQuantity = (stockUnit, contentQuantity) => (
  stockUnit === 'Unit' ? 1 : Number(contentQuantity) || 0
);

export const toPackageQuantity = (baseQuantity, contentQuantity) => {
  const content = Number(contentQuantity);
  if (!Number.isFinite(content) || content <= 0) {
    return 0;
  }

  return roundStockQuantity(Number(baseQuantity) / content);
};

export const splitStockQuantity = ({ stockOnHand, contentQuantity }) => {
  const content = Number(contentQuantity) > 0 ? Number(contentQuantity) : 1;
  const onHand = Number(stockOnHand) || 0;
  const negative = onHand < 0;
  const absolute = Math.abs(onHand);
  const packages = Math.floor((absolute + 1e-9) / content);
  const remainder = roundStockQuantity(absolute - (packages * content));

  return {
    packages,
    remainder: remainder > 0 ? remainder : 0,
    negative,
    content: roundStockQuantity(content),
  };
};

export const formatStockQuantity = ({ stockOnHand, contentQuantity, stockUnit }) => {
  const unit = stockUnit || 'Unit';
  const suffix = stockUnitSuffix(unit);
  const content = Number(contentQuantity) > 0 ? Number(contentQuantity) : 1;
  const onHand = Number(stockOnHand) || 0;

  if (unit === 'Unit' && content === 1) {
    return formatQuantityNumber(onHand);
  }

  const parts = splitStockQuantity({ stockOnHand: onHand, contentQuantity: content });
  const packageLabel = suffix
    ? `${formatQuantityNumber(parts.content)} ${suffix}`
    : formatQuantityNumber(parts.content);
  const chunks = [];

  if (parts.packages > 0) {
    chunks.push(`${parts.packages} × ${packageLabel}`);
  }

  if (parts.remainder > 0) {
    chunks.push(suffix
      ? `${formatQuantityNumber(parts.remainder)} ${suffix}`
      : formatQuantityNumber(parts.remainder));
  }

  if (chunks.length === 0) {
    return '0';
  }

  const text = chunks.join(' + ');
  return parts.negative ? `-(${text})` : text;
};
